// @ts-nocheck

import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import ResizeDetector from 'react-resize-detector';
import styled, { css } from 'styled-components';

import { useWtv } from '../../hooks';
import { __ } from '../../i18n/l10n';
import { getDimensions } from '../../lib/core/dom/dimensions';
import { isLibrary } from '../../lib/useragent';
import { selectUserPrefsShowWTV } from '../../selectors/user/userPrimary.selector';
import { Licences } from '../../types/common';
import type { Licence } from '../../types/common';
import AdsIFrameContainer from '../AdsIFrame/AdsIFrameContainer';
import BasketContainer from '../Basket/BasketContainer';
import FloatingWtvPlaceholder from '../FloatingWtvPlaceholder';
import LongPressButton from '../LongPressButton';
import MultiviewControllerContainer from '../MultiviewController/MultiviewControllerContainer';
import Sticky from '../Sticky';
import TvBarContainer from '../TvBar/TvBarContainer';

export const RightColumnWrapper = styled.div`
  ${({ $isMultiview, theme }) =>
    $isMultiview
      ? css`
          width: 450px;
        `
      : css`
          width: 350px;
          margin-left: 10px;

          @media screen and (min-width: ${theme.breakpoints.lg}px) and (max-width: ${theme.breakpoints.xl - 1}px) {
            margin-left: 53px;
          }

          @media screen and (min-width: ${theme.breakpoints.xl}px) {
            margin-left: 120px;
          }

          @media screen and (min-width: ${theme.breakpoints.sm}px) and (max-width: ${theme.breakpoints.md}px) {
            width: 275px;
          }
        `}
`;

export const StyledTvBarContainer = styled(TvBarContainer)`
  margin: 10px 0 0;
`;

export const WrapSticky = styled.div`
  padding-top: ${({ $isSticky }) => ($isSticky ? '20px' : 0)};
`;

const PanicPanel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: left;
  margin-bottom: 1.5em;
`;

const PanicDescription = styled.div`
  margin-right: 1em;
  color: white;
`;

const StyledPanicButton = styled(LongPressButton)`
  font-size: 1.2em;
  width: 9em;
`;

type StickyContentProps = {
  isMultiview: boolean;
  isSticky?: boolean;
  showWTV: boolean;
};

const StickyContent = ({ isSticky, isMultiview, showWTV }: StickyContentProps) => {
  const { setOffsetTop, setIsSticky } = useWtv();

  const onResizeBasket = (width, height) => {
    setOffsetTop(isSticky ? height : height + 20); // add 20 which refers to right column padding-top
  };

  useEffect(() => {
    setIsSticky(isSticky);
  }, [isSticky, setIsSticky]);

  return (
    <WrapSticky $isSticky={isSticky} data-testid="sticky-wrap">
      <BasketContainer />
      {!isMultiview && !isLibrary ? (
        <>
          <StyledTvBarContainer />
          {showWTV ? <FloatingWtvPlaceholder /> : null}
        </>
      ) : null}
      <AdsIFrameContainer />
      <ResizeDetector handleHeight onResize={onResizeBasket} />
    </WrapSticky>
  );
};

type DesktopBettingLayoutRightColumnProps = {
  isMultiview: boolean;
  licence: Licence;
  connected: boolean;
  realMoney: boolean;
  handlePanicButtonPress: () => void;
};

/**
 * Display the right colum for the betting section.
 *
 * @param  {boolean}  isMultiview     Check if it is multiview or not
 * @param  {string}   licence         The current licence
 * @return {ReactNode}                A styled component wrapper with the Controller container and the Sticky component,
 */
const DesktopBettingLayoutRightColumn = ({
  isMultiview,
  licence,
  connected,
  realMoney,
  handlePanicButtonPress,
}: DesktopBettingLayoutRightColumnProps) => {
  const rightColumnRef = useRef();
  const { floatingWtvRef, offsetTop } = useWtv();
  const [isStickyModeDisabled, setIsStickyModeDisabled] = useState(false);
  const showWTV = useSelector(selectUserPrefsShowWTV);
  const isLicenceDE = licence === Licences.DE;
  const disabled = isStickyModeDisabled || isLicenceDE;

  const getContainerHeight = (ref) => {
    if (ref?.current) {
      return getDimensions(ref.current).height;
    }

    return 0;
  };

  // if the right column height is superior than the browser height, we disable the sticky mode
  useEffect(
    () => {
      const floatingWtvHeight = !isMultiview ? getContainerHeight(floatingWtvRef) : 0;
      const rightColumnHeight = getContainerHeight(rightColumnRef);
      const totalRightColumnHeight = rightColumnHeight + floatingWtvHeight;

      if (totalRightColumnHeight < window.innerHeight) {
        setIsStickyModeDisabled(false);
      } else {
        setIsStickyModeDisabled(true);
      }
    },
    // add of `isMultiview`as dependency because we don't have the floating wtv on the right column, so the height is 0
    // add `showWTV` as dependency to handle the new floating wtv height when we switch on/off the WTV
    // add `offsetTop` as dependency for when we add news items to the basket
    [floatingWtvRef, isMultiview, offsetTop, showWTV],
  );

  const panicButton = useMemo(() => {
    if (isLicenceDE && connected && realMoney && !isLibrary) {
      return (
        <PanicPanel>
          <PanicDescription>{__('For a 24h break hold the button pressed for 3 seconds.')}</PanicDescription>
          <StyledPanicButton threshold={3000} onPress={handlePanicButtonPress}>
            {__('24-hour break')}
          </StyledPanicButton>
        </PanicPanel>
      );
    }
    return null;
  }, [connected, handlePanicButtonPress, isLicenceDE, realMoney]);

  return (
    <RightColumnWrapper ref={rightColumnRef} $isMultiview={isMultiview} data-testid="rightColumn">
      {isMultiview ? <MultiviewControllerContainer /> : null}
      {panicButton}
      <Sticky disabled={disabled}>
        <StickyContent isMultiview={isMultiview} showWTV={showWTV} />
      </Sticky>
    </RightColumnWrapper>
  );
};

export default DesktopBettingLayoutRightColumn;
